"use client";
// ────────── Search + Results screen — from Claude Design prototype ──────────
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { VColor, VFont, verdictTone, confidenceFromEmoji, extractVerdictTier, mapFlag, budgetTone, API_URL } from './tokens';
import type { ConfidenceLevel } from './tokens';
import {
  ConfidenceBadge, FlagBadge, PrixTypeBadge, FrictionBadge,
  VerdictPill, BudgetBar,
  Rating, Card, Eyebrow, SerifHead, VButton,
} from './ui-parts';
import { RecommendationCards } from './recommendation-cards';
import { usePreferences } from './use-preferences';
import { useHistory, useFavorites } from './use-history';
import {
  IconPlane, IconTrain, IconBuilding, IconCalendar,
  IconUsers, IconArrow, IconChevR, IconChevL, IconSwap, IconMapPin,
  IconShield, IconInfo,
} from './icons';

// ────────── Types ──────────

interface TransportLeg {
  date: string;
  dep: string;
  arr: string;
  from: string;
  to: string;
}

interface ScenarioTransport {
  op: string;
  type: 'plane' | 'train';
  out: TransportLeg;
  back: TransportLeg;
  price: number;
}

interface ScenarioStay {
  name: string;
  type: string;
  rating: number;
  reviews: number;
  nights: number;
  pricePerPerson: number;
  pricePerNight: number;
}

interface ScenarioFlag {
  kind: 'destructive' | 'info';
  label: string;
}

interface Scenario {
  id: string;
  label: string;
  total: number;
  pct: number;
  verdict: string;
  verdictNote: string;
  confidence: ConfidenceLevel;
  transport: ScenarioTransport;
  stay: ScenarioStay;
  flags: ScenarioFlag[];
  bookingUrlTransport?: string;
  bookingUrlHeb?: string;
}

interface TransportRow {
  id: string;
  src: string;
  type: 'plane' | 'train';
  verdictMsg: string;
  out: string;
  back: string;
  price: number | null;  // V3.1 P1 #3 : null = prix indisponible (pas 0€)
  priceDisplay: string;  // V3.1 P1 #3 : ex "127€" / "80-150€" / "Prix indisponible"
  confidence: ConfidenceLevel;
  flags: ScenarioFlag[];
  bookingUrl?: string;
  // V3.0 — V5.0 : 4 catégories CDC
  modePrincipal: 'avion' | 'train' | 'mixte' | 'multimodal_terrestre' | 'mixte_avion_rail' | 'bus';
  dureePaP?: number;
  frictionLabel: string;
  frictionSeverity: string;
  frictionDetail: string;  // V4.0 Phase 2 Chantier 3
  prixType: string;
  // V4.0 Phase 2 — coût total structuré
  coutTotal?: {
    billet_pp: number | null;
    surcouts: Array<{ label: string; montant_pp: number; type: string }>;
    surcout_total_pp: number;
    total_pp: number | null;
    details: string[];
  };
  // V4.0 Phase 2 — porte-à-porte détaillé
  porteAPorte?: {
    total_min: number;
    detail: Array<{ phase: string; minutes: number }>;
    source: string;
  };
}

interface StayRow {
  id: string;
  name: string;
  type: string;
  src: string;
  rating: number;
  reviews: number;
  perNight: number;
  perPerson: number;
  // V4.15 chantier 1 : prix total + partage
  totalPrice: number;
  nights: number;
  defaultSplit: number;
  confidence: ConfidenceLevel;
  flags: ScenarioFlag[];
  bookingUrl?: string;
  // V3.0
  frictionLabel: string;
  frictionSeverity: string;
  frictionDetail: string;  // V4.0 Phase 2 Chantier 3
  // V3.2
  reviewBest?: string;
  reviewBestNote?: number;
  reviewWorst?: string;
  reviewWorstNote?: number;
}

interface SearchQuery {
  from: string;
  to: string;
  toCountry?: string;  // V3.2 P2 : conserver le pays depuis autocomplete
  d1: string;
  d2: string;
  budget: number;
  people: number;
}

// ────────── Noms sources lisibles (pas de noms techniques dans l'UI) ──────────

const SOURCE_LABELS: Record<string, string> = {
  fli: "Google Flights",
  serpapi_flights: "Google Flights",
  serpapi_flights_fallback: "Google Flights",
  scrapebadger: "Google Flights",
  scrape_do: "Google Flights",
  searchapi_io: "Google Flights",
  kiwi_tequila: "Kiwi",
  agnigateway: "Aviasales",
  trvl: "Google Flights",
  duffel: "Duffel",
  trainline: "Trainline",
  trainline_static: "Trainline",
  trainline_mcp: "Trainline",
  db_transport_rest: "Deutsche Bahn",
  serpapi_hotels: "Google Hotels",
  scrapebadger_hotels: "Google Hotels",
  airbnb_mcp: "Airbnb",
  tripadvisor: "Tripadvisor",
  osm: "OpenStreetMap",
  google_places: "Google",
  liteapi: "LiteAPI",
};

function sourceLabel(src: string): string {
  return SOURCE_LABELS[src] || src;
}

const TYPE_LABELS: Record<string, string> = {
  hotel: "Hôtel",
  airbnb: "Appartement",
  apparthotel: "Appart-hôtel",
  auberge: "Auberge",
  hostel: "Auberge",
  location: "Location",
  villa: "Villa",
  guesthouse: "Maison d'hôtes",
};

function typeLabel(t: string): string {
  return TYPE_LABELS[t.toLowerCase()] || t.charAt(0).toUpperCase() + t.slice(1);
}

interface CategorieTransport {
  categorie: string;  // avion | train | multimodal_terrestre | mixte_avion_rail
  label: string;
  offres: TransportRow[];
}

interface CategorieHebergement {
  categorie: string;  // hotel | location
  label: string;
  offres: StayRow[];
}

interface MeteoJour {
  date: string;
  tempMax: number | null;
  tempMin: number | null;
  precipitationMm: number | null;
  weatherLabel: string;
  ventMaxKmh: number | null;
}

interface MeteoInfo {
  ville: string;
  resume: string;
  isClimatologie: boolean;
  jours: MeteoJour[];
}

interface SearchResult {
  recommendations: import('./recommendation-cards').Recommendation[];
  scenarios: Scenario[];
  transports: TransportRow[];
  stays: StayRow[];
  categoriesTransport: CategorieTransport[];
  categoriesHebergement: CategorieHebergement[];
  meteo: MeteoInfo | null;
  saison: string;
  reco: string;
  nbTransports: number;
  nbHebergements: number;
}

interface AutocompleteItem {
  city: string;
  country: string;
  place_id: string;
}

// ────────── Autocomplete destination ──────────

// V3.1 P2 #8 : AbortController + isMounted pour éviter race conditions et setState après démontage
function useAutocomplete() {
  const [suggestions, setSuggestions] = useState<AutocompleteItem[]>([]);
  const [show, setShow] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const sessionIdRef = useRef<string>('');
  const mountedRef = useRef(true);

  // Cleanup au démontage : annuler timer et requête en cours
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (timerRef.current) clearTimeout(timerRef.current);
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  const search = useCallback((q: string) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    // Annuler la requête précédente si encore en vol
    if (abortRef.current) abortRef.current.abort();

    if (q.length < 2) {
      setSuggestions([]);
      setShow(false);
      return;
    }

    timerRef.current = setTimeout(async () => {
      const controller = new AbortController();
      abortRef.current = controller;
      try {
        const params = new URLSearchParams({ q });
        if (sessionIdRef.current) params.set('session_id', sessionIdRef.current);
        const res = await fetch(`${API_URL}/api/autocomplete?${params}`, { signal: controller.signal });
        if (!res.ok) return;
        const data = await res.json();
        // Ne pas setter si démonté ou si la requête a été abandonnée entre-temps
        if (!mountedRef.current || controller.signal.aborted) return;
        if (data.session_id) sessionIdRef.current = data.session_id;
        setSuggestions(data.suggestions || []);
        setShow((data.suggestions || []).length > 0);
      } catch (e) {
        // AbortError attendu si nouvelle saisie ; on ignore silencieusement
        if (e instanceof Error && e.name === 'AbortError') return;
        // Autres erreurs : silencieux pour l'UX (l'autocomplete est non-bloquant)
      }
    }, 250);
  }, []);

  const clear = useCallback(() => { setSuggestions([]); setShow(false); }, []);

  return { suggestions, show, search, clear, setShow };
}

// ────────── Date helpers ──────────
// Les inputs sont type="date" → valeur toujours en ISO (YYYY-MM-DD). Pas de parsing français.

function toISODate(s: string): string {
  // Déjà en ISO grâce à type="date", on retourne tel quel
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  return '';
}

function formatDateFr(iso: string): string {
  if (!iso) return '';
  try {
    const d = new Date(iso + 'T00:00:00');
    return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' });
  } catch { return iso; }
}

// _formatDateFr : retiré (code mort, audit 23 P3 — jamais appelé)

// ────────── Search form field ──────────

const Field: React.FC<{
  label: string;
  leading?: React.ReactNode;
  trailing?: React.ReactNode;
  error?: string | null;
  children: React.ReactNode;
}> = ({ label, leading, trailing, error, children }) => (
  <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
    <span style={{
      fontFamily: VFont.sans, fontSize: 11, fontWeight: 600, color: VColor.muted,
      letterSpacing: 0.4, textTransform: 'uppercase' as const,
    }}>{label}</span>
    <span style={{
      display: 'flex', alignItems: 'center', gap: 10,
      height: 52, padding: '0 14px', borderRadius: 14,
      background: VColor.paper,
      border: `1.25px solid ${error ? VColor.bad : VColor.line}`,
      transition: 'border-color 150ms ease',
    }}>
      {leading && <span style={{ color: VColor.muted, display: 'flex' }}>{leading}</span>}
      {children}
      {trailing && <span style={{ color: VColor.muted, display: 'flex' }}>{trailing}</span>}
    </span>
    {error && (
      <span style={{ fontFamily: VFont.sans, fontSize: 11, color: VColor.bad, fontWeight: 500 }}>
        {error}
      </span>
    )}
  </label>
);

// Audit 23 P2 #16 : zone tactile 28px → 44px (minimum recommandé)
const stepBtnStyle: React.CSSProperties = {
  width: 44, height: 44, borderRadius: 999,
  background: VColor.bg, border: `1px solid ${VColor.line}`,
  fontFamily: VFont.sans, fontSize: 18, color: VColor.ink, cursor: 'pointer',
  display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1,
};

// ────────── Search form ──────────

const SearchForm: React.FC<{ onSubmit: (q: SearchQuery) => void }> = ({ onSubmit }) => {
  // V4.0 Phase 2 Chantier 4 : valeurs par défaut depuis les préférences
  const { prefs } = usePreferences();
  const [from, setFrom] = useState(() => prefs.origineDefault || 'Paris');
  const [to, setTo] = useState(() => {
    if (typeof window === 'undefined') return '';
    const params = new URLSearchParams(window.location.search);
    return params.get('to') || params.get('destination') || '';
  });
  const [toCountry, setToCountry] = useState('');
  const [d1, setD1] = useState(() => {
    const dep = new Date(); dep.setDate(dep.getDate() + 7);
    return dep.toISOString().slice(0, 10);
  });
  const [d2, setD2] = useState(() => {
    const dep = new Date(); dep.setDate(dep.getDate() + 9);
    return dep.toISOString().slice(0, 10);
  });
  const [budget, setBudget] = useState(() => String(prefs.budgetDefault || 500));
  const [people, setPeople] = useState(() => prefs.voyageursDefault || 2);
  const [touched, setTouched] = useState(false);
  const ac = useAutocomplete();

  // Auto retour = départ + 2j + focus sur retour
  const handleD1Change = (val: string) => {
    setD1(val);
    if (val) {
      const dep = new Date(val + 'T00:00:00');
      dep.setDate(dep.getDate() + 2);
      setD2(dep.toISOString().slice(0, 10));
    }
  };

  const submit = () => {
    setTouched(true);
    if (!to.trim() || !budget) return;
    ac.clear();
    onSubmit({ from, to, toCountry: toCountry || undefined, d1, d2, budget: +budget, people });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* Origin / destination paired card */}
      <div style={{
        position: 'relative', borderRadius: 14, background: VColor.paper,
        border: `1.25px solid ${touched && !to.trim() ? VColor.bad : VColor.line}`,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px' }}>
          <span style={{ color: VColor.muted, display: 'flex' }}><IconMapPin size={18} /></span>
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: VFont.sans, fontSize: 10, fontWeight: 600, color: VColor.muted, letterSpacing: 0.5, textTransform: 'uppercase' as const }}>De</div>
            <input value={from} onChange={e => setFrom(e.target.value)} style={{
              border: 'none', outline: 'none', background: 'transparent',
              fontFamily: VFont.sans, fontSize: 16, fontWeight: 500, color: VColor.ink, width: '100%',
            }} />
          </div>
        </div>
        <div style={{ height: 1, background: VColor.line, marginLeft: 42 }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', position: 'relative' }}>
          <span style={{ color: VColor.muted, display: 'flex' }}><IconMapPin size={18} /></span>
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: VFont.sans, fontSize: 10, fontWeight: 600, color: VColor.muted, letterSpacing: 0.5, textTransform: 'uppercase' as const }}>Vers</div>
            <input
              value={to}
              onChange={e => { setTo(e.target.value); ac.search(e.target.value); }}
              onFocus={() => { if (ac.suggestions.length > 0) ac.setShow(true); }}
              onBlur={() => setTimeout(() => ac.setShow(false), 200)}
              placeholder="Une ville, un pays, un rêve…" data-testid="input-destination"
              style={{
                border: 'none', outline: 'none', background: 'transparent',
                fontFamily: VFont.sans, fontSize: 16, fontWeight: 500, color: VColor.ink, width: '100%',
              }}
            />
            {/* Autocomplete dropdown */}
            {ac.show && ac.suggestions.length > 0 && (
              <div style={{
                position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50,
                background: VColor.paper, border: `1px solid ${VColor.line}`,
                borderRadius: 12, marginTop: 4, overflow: 'hidden',
                boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
              }}>
                {ac.suggestions.map((s, i) => (
                  <button key={i} onMouseDown={(e) => { e.preventDefault(); setTo(s.city); setToCountry(s.country); ac.clear(); }} style={{
                    width: '100%', padding: '10px 14px', background: 'none', border: 'none',
                    borderBottom: i < ac.suggestions.length - 1 ? `1px solid ${VColor.line2}` : 'none',
                    cursor: 'pointer', textAlign: 'left',
                    fontFamily: VFont.sans, fontSize: 14, color: VColor.ink,
                    display: 'flex', alignItems: 'center', gap: 8,
                  }}>
                    <IconMapPin size={14} style={{ color: VColor.muted }} />
                    <span style={{ fontWeight: 500 }}>{s.city}</span>
                    {s.country && <span style={{ color: VColor.muted, fontSize: 12 }}>{s.country}</span>}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        <button onClick={() => { setFrom(to); setTo(from); }} style={{
          position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)',
          width: 44, height: 44, borderRadius: 999, background: VColor.bg,
          border: `1px solid ${VColor.line}`, color: VColor.ink2, cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }} aria-label="Inverser">
          <IconSwap size={18} />
        </button>
      </div>

      {/* Dates */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <Field label="Départ" leading={<IconCalendar size={18} />}>
          <input type="date" value={d1} onChange={e => handleD1Change(e.target.value)} style={{
            flex: 1, border: 'none', outline: 'none', background: 'transparent',
            fontFamily: VFont.sans, fontSize: 16, fontWeight: 500, color: VColor.ink, minWidth: 0,
          }} />
        </Field>
        <Field label="Retour" leading={<IconCalendar size={18} />}>
          <input type="date" value={d2} onChange={e => setD2(e.target.value)} style={{
            flex: 1, border: 'none', outline: 'none', background: 'transparent',
            fontFamily: VFont.sans, fontSize: 16, fontWeight: 500, color: VColor.ink, minWidth: 0,
          }} />
        </Field>
      </div>

      {/* Budget + people */}
      <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: 10 }}>
        <Field label="Budget /pers" error={touched && !budget ? 'Indique un budget' : null}>
          <span style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 4 }}>
            <input
              type="number" value={budget} onChange={e => setBudget(e.target.value)}
              style={{
                flex: 1, border: 'none', outline: 'none', background: 'transparent',
                fontFamily: VFont.sans, fontSize: 16, fontWeight: 500, color: VColor.ink, minWidth: 0,
              }}
            />
            <span style={{ fontFamily: VFont.sans, fontSize: 15, color: VColor.muted, fontWeight: 500 }}>{'€'}</span>
          </span>
        </Field>
        <Field label="Voyageurs" leading={<IconUsers size={18} />}>
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <button
              onClick={() => setPeople(Math.max(1, people - 1))}
              aria-label="Diminuer le nombre de voyageurs"
              style={stepBtnStyle}
            >{'−'}</button>
            <span style={{ fontFamily: VFont.sans, fontSize: 16, fontWeight: 600, color: VColor.ink }}>
              {people} <span style={{ color: VColor.muted, fontWeight: 400, fontSize: 13 }}>pers.</span>
            </span>
            <button
              onClick={() => setPeople(Math.min(8, people + 1))}
              aria-label="Augmenter le nombre de voyageurs"
              style={stepBtnStyle}
            >+</button>
          </div>
        </Field>
      </div>

      <VButton variant="dark" full size="lg" onClick={submit}
        style={{ marginTop: 4, fontSize: 15 }}>
        Comparer les prix
        <IconArrow size={18} />
      </VButton>

      {/* Trust strip */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, marginTop: 4,
        fontFamily: VFont.sans, fontSize: 11, color: VColor.muted,
      }}>
        <IconShield size={12} />
        <span>10 sources comparées {'·'} vols, trains, hébergements</span>
      </div>
    </div>
  );
};

// ────────── Loading skeleton with sources ──────────

// V4.13 : liste HONNÊTE des sources réellement interrogées (api.py).
// Plus de claim gonflé "11 sources" ni de marques fictives (Booking,
// Skyscanner, Kiwi, TAP, easyJet n'ont JAMAIS été des connecteurs ;
// Duffel est désactivé sandbox). Cf. ban "11 sources" V4.8 — l'écran
// de chargement avait été oublié.
const SOURCES = [
  'Google Flights', 'Google Hotels', 'Kiwi',
  'Trainline', 'Deutsche Bahn',
  'Airbnb', 'Tripadvisor',
  'Aviasales', 'Open-Meteo', 'Places',
];

const LoadingState: React.FC<{ destination: string }> = ({ destination }) => {
  const [step, setStep] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setStep(s => Math.min(SOURCES.length, s + 1)), 200);
    return () => clearInterval(id);
  }, []);

  return (
    <div style={{ padding: '4px 20px 20px' }}>
      <div style={{
        background: VColor.paper, borderRadius: 18, border: `1px solid ${VColor.line}`,
        padding: 16, marginBottom: 14,
      }}>
        <Eyebrow>Comparaison en cours</Eyebrow>
        <div style={{ marginTop: 6, fontFamily: VFont.serif, fontSize: 22, color: VColor.ink, lineHeight: 1.1 }}>
          On épluche {step}/{SOURCES.length} sources<br /> pour {destination}…
        </div>
        <div style={{ marginTop: 14, height: 4, borderRadius: 2, background: VColor.line2, overflow: 'hidden' }}>
          <div style={{
            height: '100%', width: `${(step / SOURCES.length) * 100}%`,
            background: VColor.ink, transition: 'width 180ms linear',
          }} />
        </div>
        <div style={{ marginTop: 12, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {SOURCES.map((s, i) => (
            <span key={s} style={{
              fontFamily: VFont.mono, fontSize: 10, padding: '3px 7px', borderRadius: 999,
              background: i < step ? VColor.goodSoft : VColor.line2,
              color: i < step ? VColor.good : VColor.muted,
              fontWeight: 500, transition: 'all 180ms ease',
              opacity: i < step ? 1 : 0.6,
            }}>
              {i < step ? '✓ ' : '… '}{s}
            </span>
          ))}
        </div>
      </div>

      {[0, 1, 2].map(i => (
        <div key={i} style={{
          background: VColor.paper, borderRadius: 18, border: `1px solid ${VColor.line}`,
          padding: 16, marginBottom: 12,
          animation: `vshimmer 1.2s ease-in-out ${i * 0.15}s infinite`,
        }}>
          <div style={{ height: 10, width: '40%', background: VColor.line2, borderRadius: 4 }} />
          <div style={{ height: 28, width: '55%', background: VColor.line2, borderRadius: 4, marginTop: 10 }} />
          <div style={{ height: 8, width: '100%', background: VColor.line2, borderRadius: 999, marginTop: 14 }} />
          <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
            <div style={{ flex: 1, height: 36, background: VColor.line2, borderRadius: 8 }} />
            <div style={{ flex: 1, height: 36, background: VColor.line2, borderRadius: 8 }} />
          </div>
        </div>
      ))}
    </div>
  );
};

// ────────── Mini price history (hero card sparkline) ──────────

const HeroPriceHistory: React.FC<{ current?: number; median?: number; points?: number[] }> = ({
  current, median, points
}) => {
  // Audit 23 P2 #12-13 : ne JAMAIS afficher de données fictives.
  // Le composant ne se rend que si des vraies données historiques sont fournies.
  if (!points || points.length < 2 || current == null || median == null) return null;
  const data = points;
  const w = 320, h = 44, pad = 2;
  const min = Math.min(...data, current) - 4;
  const max = Math.max(...data, median) + 4;
  const range = Math.max(1, max - min);
  const stepX = (w - 2 * pad) / (data.length - 1);
  const pts = data.map((v, i) => [pad + i * stepX, h - ((v - min) / range) * (h - 2 * pad) - pad]);
  const d = pts.map((p, i) => (i === 0 ? `M${p[0]},${p[1]}` : `L${p[0]},${p[1]}`)).join(' ');
  const area = `${d} L${w - pad},${h} L${pad},${h} Z`;
  const lastPt = pts[pts.length - 1];
  const medY = h - ((median - min) / range) * (h - 2 * pad) - pad;

  return (
    <div style={{ marginTop: 14 }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
        fontFamily: VFont.sans, fontSize: 11, color: VColor.muted, marginBottom: 4,
      }}>
        <span style={{ fontFamily: VFont.mono, fontSize: 10, letterSpacing: 0.6 }}>HISTORIQUE 90J</span>
        <span>médiane <strong style={{ color: VColor.ink, fontWeight: 600 }}>{median}{'€'}</strong></span>
      </div>
      <svg width="100%" height={h} viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" style={{ display: 'block' }}>
        <defs>
          <linearGradient id="vfill" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor={VColor.good} stopOpacity={0.18} />
            <stop offset="100%" stopColor={VColor.good} stopOpacity={0} />
          </linearGradient>
        </defs>
        <line x1={pad} x2={w - pad} y1={medY} y2={medY} stroke={VColor.muted} strokeDasharray="2 3" strokeWidth="1" opacity="0.5" />
        <path d={area} fill="url(#vfill)" />
        <path d={d} fill="none" stroke={VColor.good} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx={lastPt[0]} cy={lastPt[1]} r="3.5" fill={VColor.good} />
        <circle cx={lastPt[0]} cy={lastPt[1]} r="6" fill={VColor.good} opacity="0.2" />
      </svg>
    </div>
  );
};

// ────────── Confidence popover ──────────

const ConfidencePopover: React.FC<{ level: ConfidenceLevel; onClose: () => void }> = ({ level, onClose }) => {
  const content: Record<ConfidenceLevel, { title: string; body: string; ok: string }> = {
    haute: {
      title: 'Confiance haute',
      body: 'Prix vu sur 3+ sources distinctes, stable depuis 6h.',
      ok: 'Tu peux réserver sereinement — le prix bouge peu sur cette fenêtre.',
    },
    moyenne: {
      title: 'Confiance moyenne',
      body: 'Prix vu sur 1 source seulement, ou écart de 5-10% entre sources. Variation possible.',
      ok: 'Vérifie le prix final avant paiement chez le partenaire.',
    },
    fragile: {
      title: 'Confiance fragile',
      body: 'Source unique, prix volatil, ou data > 24h. Le prix réel sera probablement différent.',
      ok: 'Considère cette estimation comme indicative.',
    },
  };
  const m = content[level] || content.haute;

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, zIndex: 200,
      background: 'rgba(15,15,15,0.45)',
      display: 'flex', alignItems: 'flex-end',
      animation: 'vfade 180ms ease both',
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        width: '100%', background: VColor.paper,
        borderTopLeftRadius: 22, borderTopRightRadius: 22,
        padding: '14px 22px 28px',
        animation: 'vsheet 280ms cubic-bezier(.2,.9,.3,1) both',
      }}>
        <div style={{ width: 40, height: 4, borderRadius: 2, background: VColor.line, margin: '0 auto 18px' }} />
        <Eyebrow>Méthodologie</Eyebrow>
        <SerifHead size={26} style={{ marginTop: 6 }}>{m.title}</SerifHead>
        <p style={{ fontFamily: VFont.sans, fontSize: 14, color: VColor.ink2, lineHeight: 1.55, marginTop: 12 }}>
          {m.body}
        </p>
        <div style={{
          marginTop: 14, padding: 12, borderRadius: 12,
          background: VColor.cream,
          fontFamily: VFont.sans, fontSize: 13, color: VColor.ink, lineHeight: 1.5,
        }}>
          {'→'} {m.ok}
        </div>
        <div style={{ marginTop: 18 }}>
          <Eyebrow>Niveaux</Eyebrow>
          <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
            {([
              [VColor.good, 'Haute', '3+ sources, stable'],
              [VColor.warn, 'Moyenne', 'écart entre sources'],
              [VColor.bad, 'Fragile', 'source unique, volatil'],
            ] as const).map(([c, n, s]) => (
              <div key={n} style={{
                display: 'flex', alignItems: 'center', gap: 10,
                fontFamily: VFont.sans, fontSize: 13,
              }}>
                <span style={{ width: 8, height: 8, borderRadius: 999, background: c, display: 'inline-block' }} />
                <strong style={{ fontWeight: 600, width: 70 }}>{n}</strong>
                <span style={{ color: VColor.muted }}>{s}</span>
              </div>
            ))}
          </div>
        </div>
        <VButton variant="dark" full size="lg" style={{ marginTop: 22 }} onClick={onClose}>
          {"C'est compris"}
        </VButton>
      </div>
    </div>
  );
};

// ────────── Hero scenario card ──────────

const HeroScenarioCard: React.FC<{
  s: Scenario;
  budget: number;
  onConfClick: () => void;
}> = ({ s, budget, onConfClick }) => {
  const tone = budgetTone(s.pct);
  const TransIcon = s.transport.type === 'train' ? IconTrain : IconPlane;

  return (
    <div style={{ position: 'relative' }}>
      <div style={{
        position: 'absolute', top: -10, left: 18, zIndex: 2,
        background: VColor.ink, color: VColor.cream, padding: '4px 10px',
        borderRadius: 6, fontFamily: VFont.mono, fontSize: 10,
        fontWeight: 600, letterSpacing: 1.2,
      }}>{'★'} {s.label}</div>

      <Card hero style={{ padding: 0 }}>
        <div style={{
          padding: '20px 18px 14px',
          background: `linear-gradient(180deg, ${VColor.cream} 0%, ${VColor.paper} 100%)`,
          borderBottom: `1px solid ${VColor.line2}`,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginTop: 4 }}>
            <VerdictPill text={s.verdict} sub={s.verdictNote} big />
            <ConfidenceBadge level={s.confidence} onClick={onConfClick} />
          </div>
          <div style={{ marginTop: 14, display: 'flex', alignItems: 'baseline', gap: 6 }}>
            <span style={{
              fontFamily: VFont.serif, fontSize: 64, fontWeight: 400, color: VColor.ink,
              lineHeight: 1, letterSpacing: -2,
            }}>{s.total}</span>
            <span style={{ fontFamily: VFont.serif, fontSize: 30, color: VColor.ink2 }}>{'€'}</span>
            <span style={{ fontFamily: VFont.sans, fontSize: 13, color: VColor.muted, marginLeft: 4 }}>par personne</span>
          </div>
          <div style={{ fontFamily: VFont.serif, fontSize: 16, color: VColor.ink2, marginTop: 6, fontStyle: 'italic', lineHeight: 1.35 }}>
            {'«'} {s.verdictNote} {'»'}
          </div>
          {/* Audit 23 P2 #12 : ne pas passer de médiane fictive (total*1.3) — le composant
              ne se rend que si des vrais points historiques sont fournis */}
          <HeroPriceHistory current={s.transport.price} />
          <div style={{ marginTop: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6,
              fontFamily: VFont.sans, fontSize: 11, color: VColor.muted }}>
              <span>Budget utilisé</span>
              <span style={{ color: tone, fontWeight: 600 }}>{s.pct}% de {budget}{'€'}</span>
            </div>
            <BudgetBar pct={s.pct} />
          </div>
        </div>

        <div style={{ padding: '14px 18px' }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
            <div style={{
              width: 36, height: 36, borderRadius: 10, background: VColor.cream,
              display: 'flex', alignItems: 'center', justifyContent: 'center', color: VColor.ink, flexShrink: 0,
            }}><TransIcon size={18} /></div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                <div style={{ fontFamily: VFont.sans, fontSize: 14, fontWeight: 600, color: VColor.ink }}>{s.transport.op}</div>
                <div style={{ fontFamily: VFont.sans, fontSize: 14, fontWeight: 600, color: VColor.ink }}>{s.transport.price}{'€'}</div>
              </div>
              <div style={{ fontFamily: VFont.mono, fontSize: 11, color: VColor.muted, marginTop: 4, lineHeight: 1.5 }}>
                {s.transport.out.date} {'·'} {s.transport.out.dep}{'–'}{s.transport.out.arr} {'·'} {s.transport.out.from} {'→'} {s.transport.out.to}<br />
                {s.transport.back.date} {'·'} {s.transport.back.dep}{'–'}{s.transport.back.arr} {'·'} {s.transport.back.from} {'→'} {s.transport.back.to}
              </div>
            </div>
          </div>
          <div style={{ height: 1, background: VColor.line2, margin: '14px 0 14px 48px' }} />
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
            <div style={{
              width: 36, height: 36, borderRadius: 10, background: VColor.cream,
              display: 'flex', alignItems: 'center', justifyContent: 'center', color: VColor.ink, flexShrink: 0,
            }}><IconBuilding size={18} /></div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                <div style={{ fontFamily: VFont.sans, fontSize: 14, fontWeight: 600, color: VColor.ink }}>{s.stay.name}</div>
                <div style={{ fontFamily: VFont.sans, fontSize: 14, fontWeight: 600, color: VColor.ink }}>
                  {s.stay.pricePerPerson}{'€'}<span style={{ color: VColor.muted, fontWeight: 400, fontSize: 12 }}>/pers</span>
                </div>
              </div>
              <div style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 10 }}>
                <Rating value={s.stay.rating} reviews={s.stay.reviews} />
                <span style={{ fontFamily: VFont.sans, fontSize: 12, color: VColor.muted }}>
                  {'·'} {s.stay.nights} nuits {'·'} {s.stay.type}
                </span>
              </div>
            </div>
          </div>

          {s.flags.length > 0 && (
            <div style={{ marginTop: 14, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {s.flags.map((f, i) => <FlagBadge key={i} kind={f.kind}>{f.label}</FlagBadge>)}
            </div>
          )}
        </div>

        <div style={{ padding: '0 18px 18px', display: 'grid', gridTemplateColumns: '1fr 1.3fr', gap: 8 }}>
          <VButton variant="outline" size="md" onClick={() => {
            if (s.bookingUrlTransport) window.open(s.bookingUrlTransport, '_blank');
          }}>Voir le trajet</VButton>
          <VButton variant="primary" size="md" onClick={() => {
            if (s.bookingUrlHeb) window.open(s.bookingUrlHeb, '_blank');
          }}>
            Réserver
            <IconArrow size={16} />
          </VButton>
        </div>
      </Card>
    </div>
  );
};

// ────────── Compact alt scenario card (carousel) ──────────

const AltScenarioCard: React.FC<{ s: Scenario }> = ({ s }) => {
  const TransIcon = s.transport.type === 'train' ? IconTrain : IconPlane;
  return (
    <Card style={{ width: 240, flexShrink: 0, padding: 14, scrollSnapAlign: 'start' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Eyebrow color={VColor.accent}>{s.label}</Eyebrow>
        <VerdictPill text={s.verdict} />
      </div>
      <div style={{ marginTop: 8, display: 'flex', alignItems: 'baseline', gap: 4 }}>
        <span style={{ fontFamily: VFont.serif, fontSize: 36, color: VColor.ink, lineHeight: 1, letterSpacing: -1 }}>{s.total}</span>
        <span style={{ fontFamily: VFont.serif, fontSize: 18, color: VColor.ink2 }}>{'€'}</span>
        <span style={{ fontFamily: VFont.sans, fontSize: 11, color: VColor.muted, marginLeft: 4 }}>/pers</span>
      </div>
      <div style={{ marginTop: 10 }}><BudgetBar pct={s.pct} height={6} /></div>
      <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontFamily: VFont.sans, fontSize: 12, color: VColor.ink2 }}>
          <TransIcon size={13} /> <span style={{ fontWeight: 500 }}>{s.transport.op}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontFamily: VFont.sans, fontSize: 12, color: VColor.ink2 }}>
          <IconBuilding size={13} />
          <span style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.stay.name}</span>
        </div>
      </div>
      {s.flags.length > 0 && (
        <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {s.flags.slice(0, 2).map((f, i) => <FlagBadge key={i} kind={f.kind}>{f.label}</FlagBadge>)}
          {s.flags.length > 2 && (
            <span style={{ fontFamily: VFont.sans, fontSize: 11, color: VColor.muted, alignSelf: 'center' }}>+{s.flags.length - 2}</span>
          )}
        </div>
      )}
    </Card>
  );
};

// ────────── Transport / Stay tab rows ──────────

// V3.1 P2 #6 : icône multimodale pour mode mixte
const MultimodalRowIcon: React.FC<{ size?: number }> = ({ size = 14 }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
    <IconTrain size={size} />
    <span style={{ fontSize: size * 0.6, color: VColor.muted }}>{'+'}</span>
    <IconPlane size={size} />
  </span>
);

const TransportRowCard: React.FC<{ t: TransportRow }> = ({ t }) => {
  const v = verdictTone(t.verdictMsg);
  // V5.0 : 4 modes possibles depuis le backend
  const isMixte = t.modePrincipal === 'mixte'
    || t.modePrincipal === 'mixte_avion_rail'
    || t.modePrincipal === 'multimodal_terrestre'
    || t.modePrincipal === 'bus';
  const ModeIcon = t.modePrincipal === 'train' ? IconTrain
    : isMixte ? MultimodalRowIcon
    : IconPlane;
  const modeLabel = t.modePrincipal === 'train' ? 'trajet'
    : t.modePrincipal === 'mixte_avion_rail' ? 'trajet avion+rail'
    : t.modePrincipal === 'multimodal_terrestre' ? 'trajet terrestre'
    : isMixte ? 'trajet multimodal'
    : 'vol';
  return (
    <Card style={{ padding: 14, marginBottom: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <ModeIcon size={14} />
            <span style={{ fontFamily: VFont.sans, fontSize: 13, fontWeight: 600, color: VColor.ink }}>{sourceLabel(t.src)}</span>
            {isMixte && (
              <span style={{
                fontFamily: VFont.mono, fontSize: 9, color: VColor.muted,
                letterSpacing: 0.4, textTransform: 'uppercase' as const,
                padding: '2px 6px', background: VColor.cream, borderRadius: 4,
              }}>Multimodal</span>
            )}
            <ConfidenceBadge level={t.confidence} />
            <FrictionBadge label={t.frictionLabel} severity={t.frictionSeverity} />
          </div>
          {/* V4.0 Chantier 3 — Phrase explicative friction */}
          {t.frictionDetail && (
            <div style={{
              fontFamily: VFont.sans, fontSize: 11, color: VColor.muted,
              fontStyle: 'italic', marginTop: 3, lineHeight: 1.3,
            }}>
              {t.frictionDetail}
            </div>
          )}
          <div style={{ fontFamily: VFont.mono, fontSize: 11, color: VColor.muted, marginTop: 6, lineHeight: 1.6 }}>
            {t.out}<br />{t.back}
          </div>
          {/* V4.0 — Porte-à-porte détaillé */}
          {t.porteAPorte ? (
            <div style={{ marginTop: 4 }}>
              <div style={{
                fontFamily: VFont.mono, fontSize: 10, color: VColor.accent, letterSpacing: 0.3,
              }}>
                Porte-à-porte : ~{Math.floor(t.porteAPorte.total_min / 60)}h{(t.porteAPorte.total_min % 60).toString().padStart(2, '0')}
                {t.porteAPorte.source === 'google' && (
                  <span style={{ fontSize: 9, color: VColor.muted, marginLeft: 6 }}>(Google Maps)</span>
                )}
              </div>
            </div>
          ) : t.dureePaP ? (
            <div style={{
              fontFamily: VFont.mono, fontSize: 10, color: VColor.accent,
              marginTop: 4, letterSpacing: 0.3,
            }}>
              Porte-à-porte : ~{Math.floor(t.dureePaP / 60)}h{(t.dureePaP % 60).toString().padStart(2, '0')}
            </div>
          ) : null}
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'flex-end' }}>
            {/* V3.1 P1 #3 : afficher priceDisplay backend ("127€" / fourchette / "Prix indisponible") au lieu de 0€ */}
            <div style={{
              fontFamily: t.price != null ? VFont.serif : VFont.sans,
              fontSize: t.price != null ? 26 : 13,
              color: t.price != null ? v.fg : VColor.muted,
              lineHeight: 1, letterSpacing: -0.5,
              fontStyle: t.price != null ? 'normal' : 'italic',
            }}>{t.priceDisplay}</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'flex-end', marginTop: 4 }}>
            <PrixTypeBadge type={t.prixType} />
          </div>
          {t.price != null && (
            <div style={{ fontFamily: VFont.mono, fontSize: 9, fontWeight: 600, color: v.fg, marginTop: 4, letterSpacing: 0.6 }}>
              {extractVerdictTier(t.verdictMsg)}
            </div>
          )}
        </div>
      </div>
      {t.flags.length > 0 && (
        <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {t.flags.map((f, i) => <FlagBadge key={i} kind={f.kind}>{f.label}</FlagBadge>)}
        </div>
      )}
      {/* V4.0 Phase 2 — Coût total enrichi (décomposition structurée) */}
      {t.coutTotal && t.coutTotal.surcouts.length > 0 && (
        <div style={{
          marginTop: 10, padding: '8px 12px', background: VColor.cream, borderRadius: 10,
          border: `1px solid ${VColor.line}`,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <span style={{ fontFamily: VFont.sans, fontSize: 11, fontWeight: 600, color: VColor.ink2 }}>
              Co{'\u00FB'}t r{'\u00E9'}el estim{'\u00E9'}
            </span>
            {t.coutTotal.total_pp != null && (
              <span style={{ fontFamily: VFont.serif, fontSize: 18, fontWeight: 400, color: VColor.ink }}>
                {Math.round(t.coutTotal.total_pp)}{'\u20AC'}<span style={{ fontSize: 11, color: VColor.muted }}>/pers</span>
              </span>
            )}
          </div>
          <div style={{ marginTop: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {t.coutTotal.surcouts.map((s, i) => (
              <div key={i} style={{ fontFamily: VFont.sans, fontSize: 11, color: VColor.ink2, display: 'flex', justifyContent: 'space-between' }}>
                <span>{s.label}</span>
                <span style={{ fontFamily: VFont.mono, fontSize: 10, color: VColor.warn }}>+{Math.round(s.montant_pp)}{'\u20AC'}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end' }}>
        <VButton variant="outline" size="sm" onClick={() => {
          if (t.bookingUrl) window.open(t.bookingUrl, '_blank');
        }}>Voir le {modeLabel} <IconChevR size={14} /></VButton>
      </div>
    </Card>
  );
};

// V4.15 chantier 1 : présentation prix logement — total en premier +
// stepper "partagé à X" avec calcul "ta part". Le total est la vérité
// (ce que l'hôtel/Airbnb facture), le /pers est secondaire et réglable.
const StayRowCard: React.FC<{ h: StayRow }> = ({ h }) => {
  const [split, setSplit] = useState(h.defaultSplit || 2);
  const taPart = h.totalPrice > 0 ? Math.round(h.totalPrice / split) : h.perPerson;
  return (
  <Card style={{ padding: 14, marginBottom: 10 }}>
    <div style={{ display: 'flex', gap: 12 }}>
      <div style={{
        width: 64, height: 64, borderRadius: 10, flexShrink: 0,
        background: `repeating-linear-gradient(45deg, ${VColor.cream} 0 6px, ${VColor.line2} 6px 12px)`,
        display: 'flex', alignItems: 'center', justifyContent: 'center', color: VColor.muted,
      }}>
        <IconBuilding size={20} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
          <div style={{ fontFamily: VFont.sans, fontSize: 14, fontWeight: 600, color: VColor.ink, lineHeight: 1.2 }}>{h.name}</div>
          <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
            <FrictionBadge label={h.frictionLabel} severity={h.frictionSeverity} />
            <ConfidenceBadge level={h.confidence} />
          </div>
        </div>
        <div style={{ fontFamily: VFont.sans, fontSize: 12, color: VColor.muted, marginTop: 2 }}>
          {h.type}{h.src ? ` · ${sourceLabel(h.src)}` : ""}
        </div>
        <div style={{ marginTop: 6 }}>
          <Rating value={h.rating} reviews={h.reviews} />
        </div>
        {h.frictionDetail && (
          <div style={{
            fontFamily: VFont.sans, fontSize: 11, color: VColor.muted,
            fontStyle: 'italic', marginTop: 3, lineHeight: 1.3,
          }}>
            {h.frictionDetail}
          </div>
        )}
      </div>
    </div>
    <div style={{
      marginTop: 12, paddingTop: 12, borderTop: `1px solid ${VColor.line2}`,
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    }}>
      <div>
        {/* V4.15 : Total séjour = vérité (ce que le logement facture) */}
        <div style={{ fontFamily: VFont.serif, fontSize: 22, color: VColor.ink, lineHeight: 1 }}>
          {h.totalPrice > 0 ? `${h.totalPrice}€` : "Prix indisponible"}
          <span style={{ fontFamily: VFont.sans, fontSize: 11, color: VColor.muted, marginLeft: 4 }}>
            total {h.nights > 1 ? `${h.nights} nuits` : '1 nuit'}
          </span>
        </div>
        <div style={{ fontFamily: VFont.mono, fontSize: 10, color: VColor.muted, marginTop: 3 }}>
          {h.perNight}{'€'}/nuit
        </div>
        {/* Stepper partage : "partagé à X → ta part Y€" */}
        <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontFamily: VFont.sans, fontSize: 11, color: VColor.muted }}>Partagé à</span>
          {/* Audit 23 P2 #15 : zone tactile 22px → 36px (compromis mobile) */}
          <button onClick={() => setSplit(Math.max(1, split - 1))} style={{
            width: 36, height: 36, borderRadius: 8, border: `1px solid ${VColor.line}`,
            background: VColor.paper, cursor: 'pointer', fontFamily: VFont.sans, fontSize: 15,
            display: 'flex', alignItems: 'center', justifyContent: 'center', color: VColor.ink,
          }}>−</button>
          <span style={{ fontFamily: VFont.sans, fontSize: 14, fontWeight: 600, color: VColor.ink, minWidth: 18, textAlign: 'center' as const }}>{split}</span>
          <button onClick={() => setSplit(Math.min(10, split + 1))} style={{
            width: 36, height: 36, borderRadius: 8, border: `1px solid ${VColor.line}`,
            background: VColor.paper, cursor: 'pointer', fontFamily: VFont.sans, fontSize: 15,
            display: 'flex', alignItems: 'center', justifyContent: 'center', color: VColor.ink,
          }}>+</button>
          <span style={{ fontFamily: VFont.sans, fontSize: 12, color: VColor.accent, fontWeight: 600 }}>
            → ta part : {taPart}{'€'}
          </span>
        </div>
      </div>
      <VButton variant="primary" size="sm" onClick={() => {
        if (h.bookingUrl) window.open(h.bookingUrl, '_blank');
      }}>
        {h.bookingUrl ? 'Réserver' : 'Voir'}
      </VButton>
    </div>
    {h.flags.length > 0 && (
      <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {h.flags.map((f, i) => <FlagBadge key={i} kind={f.kind}>{f.label}</FlagBadge>)}
      </div>
    )}
    {/* V4.0 Phase 2 Chantier 6 — Google Reviews best + worst (15 mots max) */}
    {(h.reviewBest || h.reviewWorst) && (
      <div style={{
        marginTop: 12, paddingTop: 10, borderTop: `1px solid ${VColor.line2}`,
        display: 'flex', flexDirection: 'column', gap: 6,
      }}>
        <Eyebrow>Avis Google</Eyebrow>
        {h.reviewBest && (
          <div style={{
            padding: '8px 12px', background: VColor.goodSoft, borderRadius: 10,
            borderLeft: `3px solid ${VColor.good}`,
          }}>
            <div style={{
              fontFamily: VFont.sans, fontSize: 12, color: VColor.ink2,
              lineHeight: 1.4, fontStyle: 'italic',
            }}>
              {'«'} {h.reviewBest} {'»'}
            </div>
            <div style={{
              fontFamily: VFont.mono, fontSize: 10, color: VColor.good,
              marginTop: 4, letterSpacing: 0.3,
            }}>
              {'★'.repeat(h.reviewBestNote || 5)}{'/5'}
            </div>
          </div>
        )}
        {h.reviewWorst && (
          <div style={{
            padding: '8px 12px', background: VColor.badSoft, borderRadius: 10,
            borderLeft: `3px solid ${VColor.bad}`,
          }}>
            <div style={{
              fontFamily: VFont.sans, fontSize: 12, color: VColor.ink2,
              lineHeight: 1.4, fontStyle: 'italic',
            }}>
              {'«'} {h.reviewWorst} {'»'}
            </div>
            <div style={{
              fontFamily: VFont.mono, fontSize: 10, color: VColor.bad,
              marginTop: 4, letterSpacing: 0.3,
            }}>
              {'★'.repeat(h.reviewWorstNote || 1)}{'/5'}
            </div>
          </div>
        )}
      </div>
    )}
  </Card>
  );
};

// ────────── API data mapper ──────────

function mapTransportRow(t: Record<string, unknown>, i: number): TransportRow {
  // V3.1 P1 #3 : ne pas afficher 0€ quand prix absent ; conserver la string backend
  const prixAffiche = ((t.prix_affiche as string) || '').trim();
  const priceStr = prixAffiche.replace(/[^\d.]/g, '');
  const parsed = parseFloat(priceStr);
  const hasPrice = !!priceStr && Number.isFinite(parsed) && parsed > 0;
  const price = hasPrice ? Math.round(parsed) : null;
  const priceDisplay = hasPrice
    ? prixAffiche
    : (prixAffiche && prixAffiche !== '0€' && prixAffiche !== 'N/A' ? prixAffiche : 'Prix indisponible');
  const mode = (t.mode_principal as string) || 'avion';
  return {
    id: `t${i}`,
    src: (t.source as string) || '',
    type: (mode === 'train' ? 'train' : 'plane') as 'plane' | 'train',
    verdictMsg: (t.verdict_message as string) || '',
    out: (t.aller_resume as string) || '',
    back: (t.retour_resume as string) || '',
    price,
    priceDisplay,
    confidence: confidenceFromEmoji((t.confiance_emoji as string) || ''),
    flags: (Array.isArray(t.flags) ? t.flags as string[] : []).map(mapFlag),
    bookingUrl: (t.booking_url as string) || undefined,
    modePrincipal: mode as TransportRow['modePrincipal'],
    dureePaP: (t.duree_porte_a_porte_min as number) || undefined,
    frictionLabel: (t.friction_label as string) || 'Fluide',
    frictionSeverity: (t.friction_severity as string) || 'good',
    frictionDetail: (t.friction_detail as string) || '',
    prixType: (t.prix_type as string) || 'estimation',
    porteAPorte: (t.porte_a_porte as Record<string, unknown>) ? {
      total_min: ((t.porte_a_porte as Record<string, unknown>).total_min as number) || 0,
      detail: ((t.porte_a_porte as Record<string, unknown>).detail as Array<{ phase: string; minutes: number }>) || [],
      source: ((t.porte_a_porte as Record<string, unknown>).source as string) || 'heuristic',
    } : undefined,
    coutTotal: (t.cout_total as Record<string, unknown>) ? {
      billet_pp: ((t.cout_total as Record<string, unknown>).billet_pp as number) ?? null,
      surcouts: ((t.cout_total as Record<string, unknown>).surcouts as Array<{ label: string; montant_pp: number; type: string }>) || [],
      surcout_total_pp: ((t.cout_total as Record<string, unknown>).surcout_total_pp as number) || 0,
      total_pp: ((t.cout_total as Record<string, unknown>).total_pp as number) ?? null,
      details: ((t.cout_total as Record<string, unknown>).details as string[]) || [],
    } : undefined,
  };
}

function mapStayRow(h: Record<string, unknown>, i: number, query: SearchQuery): StayRow {
  return {
    id: `h${i}`,
    name: (h.nom as string) || '',
    src: (h.source as string) || '',
    type: typeLabel((h.type as string) || ''),
    rating: (h.note as number) || 0,
    reviews: (h.nb_avis as number) || 0,
    perNight: (h.prix_nuit as number) || 0,
    perPerson: (h.prix_par_personne as number) || 0,
    totalPrice: (h.prix_total_eur as number) || 0,
    nights: (h.nb_nuits as number) || 1,
    defaultSplit: query.people || 2,
    confidence: confidenceFromEmoji((h.confiance_emoji as string) || ''),
    flags: (Array.isArray(h.flags) ? h.flags as string[] : []).map(mapFlag),
    bookingUrl: (h.booking_url as string) || undefined,
    frictionLabel: (h.friction_label as string) || 'Fluide',
    frictionSeverity: (h.friction_severity as string) || 'good',
    frictionDetail: (h.friction_detail as string) || '',
    reviewBest: (h.review_best as string) || undefined,
    reviewBestNote: (h.review_best_note as number) || undefined,
    reviewWorst: (h.review_worst as string) || undefined,
    reviewWorstNote: (h.review_worst_note as number) || undefined,
  };
}

function mapApiToResults(data: Record<string, unknown>, query: SearchQuery): SearchResult {
  const raw = data as {
    scenarios: Array<Record<string, unknown>>;
    transports: Array<Record<string, unknown>>;
    hebergements: Array<Record<string, unknown>>;
    saison: string;
    reco_reservation: string;
    nb_transports_total: number;
    nb_hebergements_total: number;
  };

  const nights = (() => {
    if (query.d1 && query.d2) {
      const d1 = new Date(query.d1 + 'T00:00:00');
      const d2 = new Date(query.d2 + 'T00:00:00');
      return Math.max(1, Math.round((d2.getTime() - d1.getTime()) / 86400000));
    }
    return 2;
  })();

  const scenarios: Scenario[] = (raw.scenarios || []).map((s, i) => {
    const transportEur = (s.transport_eur as number) || 0;
    const hebPart = (s.heb_part_eur as number) || 0;
    const total = (s.total_par_personne as number) || transportEur + hebPart;
    const pct = (s.pct_budget as number) || (query.budget ? Math.round(total / query.budget * 100) : 0);
    const verdictMsg = (s.verdict_message as string) || '';
    const verdict = extractVerdictTier(verdictMsg);
    const confidence = confidenceFromEmoji((s.confiance_emoji as string) || '');
    const flags: ScenarioFlag[] = ((s.flags as string[]) || []).map(mapFlag);

    const labels = ['MEILLEUR DEAL', 'Moins cher', 'Mieux noté', 'Plus rapide', 'Alternative'];
    const transportResume = (s.transport_resume as string) || '';

    return {
      id: `s${i}`,
      label: labels[i] || labels[4],
      total: Math.round(total),
      pct,
      verdict,
      verdictNote: verdictMsg.split('—').pop()?.trim() || verdictMsg,
      confidence,
      transport: {
        op: transportResume,
        type: transportResume.toLowerCase().includes('train') ? 'train' as const : 'plane' as const,
        out: { date: query.d1, dep: '', arr: '', from: query.from.slice(0, 3).toUpperCase(), to: query.to.slice(0, 3).toUpperCase() },
        back: { date: query.d2, dep: '', arr: '', from: query.to.slice(0, 3).toUpperCase(), to: query.from.slice(0, 3).toUpperCase() },
        price: Math.round(transportEur),
      },
      stay: {
        name: (s.hebergement_nom as string) || 'Hébergement',
        type: '',
        rating: 0,
        reviews: 0,
        nights,
        pricePerPerson: Math.round(hebPart),
        pricePerNight: Math.round(hebPart / nights),
      },
      flags,
      bookingUrlTransport: (s.booking_url_transport as string) || undefined,
      bookingUrlHeb: (s.booking_url_heb as string) || undefined,
    };
  });

  const transports: TransportRow[] = (raw.transports || []).map((t, i) => mapTransportRow(t, i));

  const stays: StayRow[] = (raw.hebergements || []).map((h, i) => mapStayRow(h, i, query));

  // V5.0 — catégories backend (4 transport + 2 hébergement, top 5 chacune)
  const rawCatsT = ((data as Record<string, unknown>).categories_transport as Array<Record<string, unknown>>) || [];
  const categoriesTransport: CategorieTransport[] = rawCatsT.map(c => ({
    categorie: (c.categorie as string) || '',
    label: (c.label as string) || '',
    offres: (Array.isArray(c.offres) ? c.offres as Array<Record<string, unknown>> : []).map((t, i) => mapTransportRow(t, i)),
  }));

  const rawCatsH = ((data as Record<string, unknown>).categories_hebergement as Array<Record<string, unknown>>) || [];
  const categoriesHebergement: CategorieHebergement[] = rawCatsH.map(c => ({
    categorie: (c.categorie as string) || '',
    label: (c.label as string) || '',
    offres: (Array.isArray(c.offres) ? c.offres as Array<Record<string, unknown>> : []).map((h, i) => mapStayRow(h, i, query)),
  }));

  // V5.0 — météo destination
  const rawMeteo = (data as Record<string, unknown>).meteo as Record<string, unknown> | null;
  const meteo: MeteoInfo | null = rawMeteo ? {
    ville: (rawMeteo.ville as string) || '',
    resume: (rawMeteo.resume as string) || '',
    isClimatologie: !!(rawMeteo.is_climatologie as boolean),
    jours: (Array.isArray(rawMeteo.jours) ? rawMeteo.jours as Array<Record<string, unknown>> : []).map(j => ({
      date: (j.date as string) || '',
      tempMax: (j.temp_max as number) ?? null,
      tempMin: (j.temp_min as number) ?? null,
      precipitationMm: (j.precipitation_mm as number) ?? null,
      weatherLabel: (j.weather_label as string) || 'N/A',
      ventMaxKmh: (j.vent_max_kmh as number) ?? null,
    })),
  } : null;

  // Enrich scenarios with hebergement data
  for (const sc of scenarios) {
    const matchHeb = stays.find(h => h.name === sc.stay.name);
    if (matchHeb) {
      sc.stay.type = matchHeb.type;
      sc.stay.rating = matchHeb.rating;
      sc.stay.reviews = matchHeb.reviews;
      if (matchHeb.perNight > 0) sc.stay.pricePerNight = matchHeb.perNight;
    }
  }

  // V3.0 — Recommendations
  const rawRecos = ((data as Record<string, unknown>).recommendations as Array<Record<string, unknown>>) || [];
  const recommendations = rawRecos.map(r => ({
    profil: (r.profil as string) || '',
    label: (r.label as string) || '',
    icon: (r.icon as string) || 'star',
    transportResume: (r.transport_resume as string) || '',
    transportPrix: (r.transport_prix as number) ?? null,
    hebergementNom: (r.hebergement_nom as string) || '',
    hebergementPrixPP: (r.hebergement_prix_pp as number) ?? null,
    totalParPersonne: (r.total_par_personne as number) ?? null,
    dureeAllerMin: (r.duree_aller_min as number) ?? null,
    dureeTotalMin: (r.duree_total_min as number) ?? null,           // V3.1 P2 #10
    explication: (r.explication as string) || '',
    modePrincipal: (r.mode_principal as string) || 'avion',
    frictionLabel: (r.friction_label as string) || 'Fluide',
    frictionSeverity: (r.friction_severity as string) || 'good',
    frictionDetail: (r.friction_detail as string) || '',
    prixType: (r.prix_type as string) || 'estimation',              // V3.1 P2 #7
    bookingUrlTransport: (r.booking_url_transport as string) || undefined,
    bookingUrlHeb: (r.booking_url_heb as string) || undefined,
  }));

  return {
    recommendations,
    scenarios,
    transports,
    stays,
    categoriesTransport,
    categoriesHebergement,
    meteo,
    saison: raw.saison || '',
    reco: raw.reco_reservation || '',
    nbTransports: raw.nb_transports_total || transports.length,
    nbHebergements: raw.nb_hebergements_total || stays.length,
  };
}

// ────────── Results view ──────────

const ctxLabelStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 8,
  fontFamily: VFont.mono, fontSize: 10, fontWeight: 500,
  color: VColor.ink2, letterSpacing: 1.4, textTransform: 'uppercase' as const,
};

const ctxDotStyle = (color: string): React.CSSProperties => ({
  width: 6, height: 6, borderRadius: 999, background: color, display: 'inline-block',
});

const carouselBtnStyle: React.CSSProperties = {
  width: 30, height: 30, borderRadius: 999, background: VColor.paper,
  border: `1px solid ${VColor.line}`, color: VColor.ink, cursor: 'pointer',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
};

const ResultsView: React.FC<{
  query: SearchQuery;
  result: SearchResult;
  onBack: () => void;
}> = ({ query, result, onBack }) => {
  // V3.1 P1 #2 : onglet par défaut adapté à ce qu'on a réellement
  // V4.13 : on atterrit sur les volets Avion/Train (séparation des modes),
  // pas sur les scénarios fusionnés (demande utilisateur récurrente).
  const defaultTab = result.transports.length > 0 ? 'transports'
    : result.scenarios.length > 0 ? 'scenarios'
    : 'hebergements';
  const [tab, setTab] = useState(defaultTab);
  const [showConf, setShowConf] = useState(false);
  const carouselRef = useRef<HTMLDivElement>(null);
  const hero = result.scenarios[0];
  const alts = result.scenarios.slice(1);
  // V4.0 Fix Codex : favoris branchés dans les résultats
  const { addFavorite: addFav, removeFavorite: rmFav, isFavorite: isFav } = useFavorites();
  const isCurrentFav = isFav(query.from, query.to, query.d1, query.d2);

  // V3.1 P1 #2 : ne plus sortir si pas de scénario complet — afficher au moins
  // les recommandations + onglets transports/hébergements même sans hôtel.
  const hasAnyResult =
    result.scenarios.length > 0
    || result.transports.length > 0
    || result.stays.length > 0
    || result.recommendations.length > 0;

  // Audit 23 P1 #1-2 : distinguer erreur réseau du vrai zéro résultat
  const isFetchError = (result as any)?._fetchError;
  const errorMsg = (result as any)?._errorMsg;

  if (!hasAnyResult) {
    return (
      <div style={{ padding: "40px 20px", textAlign: "center" }}>
        {isFetchError ? (
          <>
            <SerifHead size={26}>Erreur de connexion</SerifHead>
            <p style={{ fontFamily: VFont.sans, fontSize: 14, color: VColor.bad, marginTop: 10 }}>
              {"Impossible de contacter le serveur"}{errorMsg ? ` (${errorMsg})` : ""}.
            </p>
            <p style={{ fontFamily: VFont.sans, fontSize: 13, color: VColor.muted, marginTop: 6 }}>
              {"Vérifie ta connexion internet ou réessaie dans quelques instants."}
            </p>
          </>
        ) : (
          <>
            <SerifHead size={26}>{"Aucun résultat"}</SerifHead>
            <p style={{ fontFamily: VFont.sans, fontSize: 14, color: VColor.muted, marginTop: 10 }}>
              {"Essaie d'autres dates ou une autre destination."}
            </p>
          </>
        )}
        <VButton variant="dark" size="md" style={{ marginTop: 20 }} onClick={onBack}>
          {isFetchError ? "Réessayer" : "Nouvelle recherche"}
        </VButton>
      </div>
    );
  }

  // V3.1 P3 #12 : titre éditorial dynamique selon les modes présents
  const modesPresent = new Set(result.transports.map(t => t.modePrincipal));
  const hasAvion = modesPresent.has('avion') || modesPresent.has('mixte');
  const hasTrain = modesPresent.has('train');
  const transportLabel = hasAvion && hasTrain ? 'trajets'
    : hasTrain ? 'trains'
    : hasAvion ? 'vols'
    : 'trajets';

  return (
    <div style={{ paddingBottom: 24 }}>
      {/* Top context bar */}
      <div style={{ padding: '8px 20px 4px', display: 'flex', alignItems: 'center', gap: 8 }}>
        <button
          onClick={onBack}
          aria-label="Retour à la recherche"
          style={{
            width: 36, height: 36, borderRadius: 999, background: VColor.paper,
            border: `1px solid ${VColor.line}`, color: VColor.ink,
            display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
          }}
        >
          <IconChevL size={18} />
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: VFont.sans, fontSize: 11, color: VColor.muted, fontWeight: 500 }}>
            {query.from} {'→'} {query.to}
          </div>
          <div style={{ fontFamily: VFont.sans, fontSize: 13, fontWeight: 600, color: VColor.ink }}>
            {formatDateFr(query.d1)} {"–"} {formatDateFr(query.d2)} {"·"} {query.people} pers {"·"} {query.budget}{"€"}
          </div>
        </div>
        {/* V4.0 Fix Codex : bouton favori */}
        <button
          onClick={() => {
            if (isCurrentFav) {
              const id = `${query.from.toLowerCase()}_${query.to.toLowerCase()}_${query.d1}_${query.d2}`;
              rmFav(id);
            } else {
              addFav({ from: query.from, to: query.to, d1: query.d1, d2: query.d2, budget: query.budget, people: query.people });
            }
          }}
          aria-label={isCurrentFav ? 'Retirer des favoris' : 'Ajouter aux favoris'}
          style={{
            width: 36, height: 36, borderRadius: 999, background: VColor.paper,
            border: `1px solid ${isCurrentFav ? VColor.accent : VColor.line}`,
            color: isCurrentFav ? VColor.accent : VColor.muted,
            display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
            fontSize: 18,
          }}
        >
          {isCurrentFav ? '★' : '☆'}
        </button>
      </div>

      {/* Context badges */}
      <div style={{ padding: '8px 20px', display: 'flex', flexWrap: 'wrap', gap: 14 }}>
        {result.saison && (
          <span style={ctxLabelStyle}>
            <span style={ctxDotStyle(VColor.good)} /> {result.saison}
          </span>
        )}
        {result.reco && (
          <span style={ctxLabelStyle}>
            <span style={ctxDotStyle(VColor.good)} /> {result.reco}
          </span>
        )}
        {result.meteo && result.meteo.resume && (
          <span style={ctxLabelStyle} title={`Météo ${result.meteo.ville}`}>
            <span style={ctxDotStyle(VColor.accent)} /> Météo : {result.meteo.resume}
          </span>
        )}
      </div>

      {/* Editorial head */}
      <div style={{ padding: '10px 20px 16px' }}>
        <Eyebrow>{result.nbTransports} {transportLabel} {'·'} {result.nbHebergements} hébergements {'·'} 10 sources</Eyebrow>
        <SerifHead size={32} style={{ marginTop: 4 }}>
          {result.scenarios.length > 0 ? (
            <>On a trouvé <em style={{ fontStyle: 'italic', color: VColor.accent }}>{result.scenarios.length} scénarios complets</em><br />pour {query.to}.</>
          ) : result.transports.length > 0 ? (
            <>On a trouvé <em style={{ fontStyle: 'italic', color: VColor.accent }}>{result.transports.length} {transportLabel}</em><br />pour {query.to}.</>
          ) : (
            <>Quelques options <em style={{ fontStyle: 'italic', color: VColor.accent }}>pour {query.to}</em>.</>
          )}
        </SerifHead>
      </div>

      {/* V3.0/V3.1 — Recommendation cards (5 profils) */}
      {result.recommendations && result.recommendations.length > 0 ? (
        <div style={{ padding: '8px 0 0' }}>
          <RecommendationCards recommendations={result.recommendations} />
        </div>
      ) : hero ? (
        <>
          {/* Fallback : ancien hero card si pas de recommendations mais on a un scénario */}
          <div style={{ padding: '8px 20px 0' }}>
            <HeroScenarioCard s={hero} budget={query.budget} onConfClick={() => setShowConf(true)} />
          </div>
          {alts.length > 0 && (
            <div style={{ padding: '24px 0 0' }}>
              <div style={{ padding: '0 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 12 }}>
                <div>
                  <Eyebrow>Sinon</Eyebrow>
                  <div style={{ fontFamily: VFont.serif, fontSize: 22, color: VColor.ink, marginTop: 2 }}>
                    {alts.length} alternative{alts.length > 1 ? 's' : ''}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button
                    onClick={() => carouselRef.current?.scrollBy({ left: -260, behavior: 'smooth' })}
                    aria-label="Faire défiler les alternatives vers la gauche"
                    style={carouselBtnStyle}
                  ><IconChevL size={14} /></button>
                  <button
                    onClick={() => carouselRef.current?.scrollBy({ left: 260, behavior: 'smooth' })}
                    aria-label="Faire défiler les alternatives vers la droite"
                    style={carouselBtnStyle}
                  ><IconChevR size={14} /></button>
                </div>
              </div>
              <div ref={carouselRef} className="scrollbar-hide" style={{
                display: 'flex', gap: 10, overflowX: 'auto',
                scrollSnapType: 'x mandatory', padding: '0 20px 8px',
              }}>
                {alts.map(s => <AltScenarioCard key={s.id} s={s} />)}
              </div>
            </div>
          )}
        </>
      ) : null}
      {showConf && hero && <ConfidencePopover level={hero.confidence} onClose={() => setShowConf(false)} />}

      {/* Details tabs */}
      <div style={{ padding: '24px 20px 0' }}>
        <div role="tablist" aria-label="Sections des résultats" style={{
          display: 'inline-flex', padding: 4, background: VColor.line2, borderRadius: 12, marginBottom: 14,
        }}>
          {(['scenarios', 'transports', 'hebergements'] as const).map(k => (
            <button
              key={k}
              role="tab"
              aria-selected={tab === k}
              aria-controls={`tabpanel-${k}`}
              onClick={() => setTab(k)}
              style={{
                padding: '8px 14px', borderRadius: 8, border: 'none', cursor: 'pointer',
                background: tab === k ? VColor.paper : 'transparent',
                color: tab === k ? VColor.ink : VColor.muted,
                fontFamily: VFont.sans, fontSize: 13, fontWeight: 600,
                boxShadow: tab === k ? '0 1px 2px rgba(0,0,0,0.05)' : 'none',
              }}
            >
              {k === 'scenarios' ? `★ Scénarios` : k === 'transports' ? `Trajets · ${result.transports.length}` : `Héb. · ${result.stays.length}`}
            </button>
          ))}
        </div>

        {tab === 'scenarios' && (
          <div>
            <div style={{ fontFamily: VFont.sans, fontSize: 13, color: VColor.muted, padding: 12,
              background: VColor.cream, borderRadius: 12, lineHeight: 1.6, marginBottom: 14 }}>
              <IconInfo size={14} style={{ verticalAlign: '-2px', marginRight: 6 }} />
              Les sc{'\u00E9'}narios combinent un trajet et un h{'\u00E9'}bergement. Bascule sur <strong>Trajets</strong> ou <strong>H{'\u00E9'}b.</strong> pour mixer toi-m{'\u00EA'}me.
            </div>
            {/* V3.2 P2 : afficher les scénarios reçus au lieu de juste le message */}
            {result.scenarios.map((s) => (
              <Card key={s.id} style={{ padding: 14, marginBottom: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontFamily: VFont.sans, fontSize: 13, fontWeight: 600, color: VColor.ink }}>
                      {s.transport.op}
                    </div>
                    <div style={{ fontFamily: VFont.sans, fontSize: 12, color: VColor.muted, marginTop: 2 }}>
                      + {s.stay.name}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontFamily: VFont.serif, fontSize: 22, color: VColor.ink }}>
                      {s.total}{'\u20AC'}<span style={{ fontSize: 12, color: VColor.muted }}>/pers</span>
                    </div>
                    {s.pct > 0 && (
                      <div style={{ fontFamily: VFont.mono, fontSize: 10, color: VColor.muted, marginTop: 2 }}>
                        {s.pct}% du budget
                      </div>
                    )}
                  </div>
                </div>
                {s.flags.length > 0 && (
                  <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {s.flags.slice(0, 2).map((f, j) => <FlagBadge key={j} kind={f.kind}>{f.label}</FlagBadge>)}
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}
        {tab === 'transports' && (() => {
          // V5.0 : 4 catégories CDC depuis le backend (top 5 chacune) :
          // avion / train / multimodal terrestre / mixte avion+rail.
          // Les 4 catégories sont TOUJOURS affichées (CDC : voir pour arbitrer).
          const cats = result.categoriesTransport || [];
          // Fallback : si le backend n'a pas renvoyé de catégories (ancien
          // sessionStorage, erreur), retomber sur l'ancien rendu plat.
          if (cats.length === 0) {
            return <div>{(result.transports || []).map(t => <TransportRowCard key={t.id} t={t} />)}</div>;
          }
          const byCat = (k: string) => cats.find(c => c.categorie === k);
          const emptyMsg = (txt: string) => (
            <div style={{
              fontFamily: VFont.sans, fontSize: 12.5, color: VColor.muted,
              padding: '14px 12px', background: VColor.cream, borderRadius: 12,
              lineHeight: 1.5,
            }}>{txt}</div>
          );
          const ICONS: Record<string, React.ReactNode> = {
            avion: <IconPlane size={14} style={{ color: VColor.ink2 }} />,
            train: <IconTrain size={14} style={{ color: VColor.ink2 }} />,
            multimodal_terrestre: <MultimodalRowIcon size={14} />,
            mixte_avion_rail: <MultimodalRowIcon size={14} />,
          };
          const LABELS: Record<string, string> = {
            avion: 'Avion', train: 'Train uniquement',
            multimodal_terrestre: 'Multimodal terrestre', mixte_avion_rail: 'Avion + Rail',
          };
          const EMPTY: Record<string, string> = {
            avion: `Aucun vol trouvé pour ${query.from} → ${query.to} sur ces dates.`,
            train: `Aucun train trouvé pour ${query.from} → ${query.to} (pas de desserte directe ou hors couverture des sources rail).`,
            multimodal_terrestre: `Aucune option multimodale terrestre (train+bus, covoiturage) pour ${query.from} → ${query.to}.`,
            mixte_avion_rail: `Aucune combinaison avion+rail pour ${query.from} → ${query.to}.`,
          };
          const renderBloc = (catKey: string, first: boolean) => {
            const cat = byCat(catKey);
            const offres = cat?.offres || [];
            return (
              <div key={catKey} style={{ marginTop: first ? 0 : 20 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
                  {ICONS[catKey]}
                  <Eyebrow color={VColor.ink2}>{LABELS[catKey]} · {offres.length}</Eyebrow>
                </div>
                {offres.length > 0
                  ? offres.map(t => <TransportRowCard key={t.id} t={t} />)
                  : emptyMsg(EMPTY[catKey])}
              </div>
            );
          };
          return (
            <div>
              {renderBloc('avion', true)}
              {renderBloc('train', false)}
              {renderBloc('multimodal_terrestre', false)}
              {renderBloc('mixte_avion_rail', false)}
            </div>
          );
        })()}
        {tab === 'hebergements' && (() => {
          // V5.0 : 2 catégories CDC (hôtels / locations, top 5 chacune), toujours affichées.
          const cats = result.categoriesHebergement || [];
          // Fallback ancien rendu plat si pas de catégories
          if (cats.length === 0) {
            return <div>{(result.stays || []).map(h => <StayRowCard key={h.id} h={h} />)}</div>;
          }
          const byCat = (k: string) => cats.find(c => c.categorie === k);
          const H_LABELS: Record<string, string> = { hotel: 'Hôtels', location: 'Locations' };
          const H_EMPTY: Record<string, string> = {
            hotel: `Aucun hôtel avec prix trouvé pour ${query.to}.`,
            location: `Aucune location (Airbnb/Booking) avec prix trouvée pour ${query.to}.`,
          };
          const renderHeb = (catKey: string, first: boolean) => {
            const cat = byCat(catKey);
            const offres = cat?.offres || [];
            return (
              <div key={catKey} style={{ marginTop: first ? 0 : 20 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
                  <IconBuilding size={14} style={{ color: VColor.ink2 }} />
                  <Eyebrow color={VColor.ink2}>{H_LABELS[catKey] || catKey} · {offres.length}</Eyebrow>
                </div>
                {offres.length > 0
                  ? offres.map(h => <StayRowCard key={h.id} h={h} />)
                  : (
                    <div style={{
                      fontFamily: VFont.sans, fontSize: 12.5, color: VColor.muted,
                      padding: '14px 12px', background: VColor.cream, borderRadius: 12, lineHeight: 1.5,
                    }}>{H_EMPTY[catKey]}</div>
                  )}
              </div>
            );
          };
          return (
            <div>
              {renderHeb('hotel', true)}
              {renderHeb('location', false)}
            </div>
          );
        })()}
      </div>
    </div>
  );
};

// ────────── Main search screen ──────────

const SESSION_KEY = 'voyage_search_result';

// V3.1 : restore sessionStorage dans useState initializers (fix React 19 set-state-in-effect)
function _restoreSession(): { phase: 'form' | 'loading' | 'results'; query: SearchQuery | null; result: SearchResult | null } {
  if (typeof window === 'undefined') return { phase: 'form', query: null, result: null };
  try {
    const saved = sessionStorage.getItem(SESSION_KEY);
    if (saved) {
      const { query: sq, result: sr } = JSON.parse(saved);
      if (sq && sr) return { phase: 'results', query: sq, result: sr };
    }
  } catch { /* ignore */ }
  return { phase: 'form', query: null, result: null };
}

export default function SearchScreen() {
  const initial = _restoreSession();
  const [phase, setPhase] = useState<'form' | 'loading' | 'results'>(initial.phase);
  const [query, setQuery] = useState<SearchQuery | null>(initial.query);
  const [result, setResult] = useState<SearchResult | null>(initial.result);
  const scrollRef = useRef<HTMLDivElement>(null);
  // V4.0 Phase 2 — historique + favoris + préférences
  const { history, addEntry: addToHistory } = useHistory();
  const { addFavorite, removeFavorite, isFavorite } = useFavorites();
  const { prefs } = usePreferences();
  // V4.1 Fix 2.7 : AbortController pour annuler la recherche précédente
  const searchAbortRef = useRef<AbortController | null>(null);

  const handleSubmit = async (q: SearchQuery) => {
    // Annuler la recherche précédente si encore en cours
    if (searchAbortRef.current) searchAbortRef.current.abort();
    const controller = new AbortController();
    searchAbortRef.current = controller;

    setQuery(q);
    setPhase('loading');
    addToHistory({ from: q.from, to: q.to, d1: q.d1, d2: q.d2, budget: q.budget, people: q.people });

    try {
      const isoD1 = toISODate(q.d1);
      const isoD2 = toISODate(q.d2);
      if (!isoD1 || !isoD2) throw new Error('Dates invalides');

      const res = await fetch(`${API_URL}/api/search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        signal: controller.signal,  // V4.1 Fix 2.7 : annulable
        body: JSON.stringify({
          origine: q.from,
          destination: q.to,
          pays_destination: q.toCountry || null,
          date_depart: isoD1,
          date_retour: isoD2,
          nb_voyageurs: q.people,
          nb_voyageurs_heb: q.people,
          budget_max_eur: q.budget,
          profil: prefs.profil,
        }),
      });
      // V4.1 : ne pas traiter si annulé entre-temps
      if (controller.signal.aborted) return;

      if (!res.ok) throw new Error(`API error ${res.status}`);
      const data = await res.json();
      const mapped = mapApiToResults(data, q);

      // V4.1 Fix 2.5 : appliquer les préférences de confort côté client
      // V5.0 : filtrer AUSSI les catégories (l'affichage les consomme directement)
      const filtreConfort = (t: TransportRow): boolean => {
        if (prefs.eviterExcentres && (t.frictionLabel === 'Risqué' || t.frictionDetail.includes('excentré'))) return false;
        if (prefs.bagageCabineOnly && t.frictionDetail.includes('bagage soute')) return false;
        return true;
      };
      if (prefs.eviterExcentres || prefs.bagageCabineOnly) {
        mapped.transports = mapped.transports.filter(filtreConfort);
        mapped.categoriesTransport = mapped.categoriesTransport.map(c => ({
          ...c, offres: c.offres.filter(filtreConfort),
        }));
      }
      if (prefs.modePreference === 'train') {
        const trains = mapped.transports.filter(t => t.modePrincipal === 'train');
        const rest = mapped.transports.filter(t => t.modePrincipal !== 'train');
        mapped.transports = [...trains, ...rest];
      } else if (prefs.modePreference === 'avion') {
        const avions = mapped.transports.filter(t => t.modePrincipal === 'avion' || t.modePrincipal === 'mixte');
        const rest = mapped.transports.filter(t => t.modePrincipal === 'train');
        mapped.transports = [...avions, ...rest];
      }

      setResult(mapped);
      setPhase('results');

      // Persist
      try { sessionStorage.setItem(SESSION_KEY, JSON.stringify({ query: q, result: mapped })); } catch { /* ignore */ }

      // Scroll to top
      setTimeout(() => {
        scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
      }, 50);
    } catch (err) {
      // V4.1 Fix 2.7 : AbortError = nouvelle recherche lancée, ignorer silencieusement
      if (err instanceof Error && err.name === 'AbortError') return;
      console.error('[search]', err);
      const msg = err instanceof Error ? err.message : 'Erreur inconnue';
      // Audit 23 P1 #1-2 : distinguer erreur réseau du vrai zéro résultat
      setResult({
        recommendations: [], scenarios: [], transports: [], stays: [],
        categoriesTransport: [], categoriesHebergement: [], meteo: null,
        saison: '', reco: msg.includes('422') ? 'Paramètres invalides' : `Erreur : ${msg}`,
        nbTransports: 0, nbHebergements: 0,
        _fetchError: true, _errorMsg: msg,  // marqueurs pour l'affichage erreur
      } as any);
      setPhase('results');
    }
  };

  const handleBack = () => {
    setPhase('form');
    try { sessionStorage.removeItem(SESSION_KEY); } catch { /* ignore */ }
  };

  return (
    <div ref={scrollRef} style={{ height: '100%', overflowY: 'auto', background: VColor.bg }}>
      {phase === 'form' && (
        <div style={{ padding: '4px 20px 110px' }}>
          <div style={{ marginTop: 14, marginBottom: 22 }}>
            <Eyebrow>Voyage · Paris</Eyebrow>
            <SerifHead size={36} style={{ marginTop: 6 }}>
              Comparez. Décidez.<br />
              <em style={{ fontStyle: 'italic', color: VColor.accent }}>Voyagez mieux.</em>
            </SerifHead>
            <p style={{
              fontFamily: VFont.sans, fontSize: 14, color: VColor.ink2, lineHeight: 1.45,
              marginTop: 10, maxWidth: 320,
            }}>
              10 sources agrégées · pièges tarifaires détectés · verdict prix historique.
            </p>
          </div>
          <SearchForm onSubmit={handleSubmit} />

          {/* V4.0 Phase 2 Chantier 5 — Dernières recherches */}
          {history.length > 0 && (
            <div style={{ marginTop: 24 }}>
              <Eyebrow>Tes dernières recherches</Eyebrow>
              <div style={{
                marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 8,
              }}>
                {history.slice(0, 5).map(h => (
                  <button
                    key={h.id}
                    onClick={() => handleSubmit({
                      from: h.from, to: h.to, d1: h.d1, d2: h.d2,
                      budget: h.budget, people: h.people,
                    })}
                    aria-label={`Relancer la recherche ${h.from} vers ${h.to}`}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 6,
                      padding: '8px 14px', borderRadius: 10,
                      background: VColor.paper, border: `1px solid ${VColor.line}`,
                      cursor: 'pointer', fontFamily: VFont.sans, fontSize: 13,
                      color: VColor.ink, fontWeight: 500,
                      transition: 'border-color 150ms ease',
                    }}
                  >
                    <span style={{ color: VColor.accent, fontWeight: 600 }}>{h.to}</span>
                    <span style={{ color: VColor.muted, fontSize: 11 }}>
                      {h.d1.slice(5).replace('-', '/')}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      {phase === 'loading' && <LoadingState destination={query?.to || ''} />}
      {phase === 'results' && query && result && (
        <ResultsView query={query} result={result} onBack={handleBack} />
      )}
    </div>
  );
}
